/**
 * AI Configuration Service
 * Manages API keys stored in browser localStorage
 */

const AI_API_KEY_STORAGE_KEY = 'quantquack_ai_api_key';
const AI_PROVIDER_STORAGE_KEY = 'quantquack_ai_provider';
const AI_MODEL_STORAGE_KEY = 'quantquack_ai_model';
const AI_AUTO_ANALYZE_STORAGE_KEY = 'quantquack_ai_auto_analyze';

export type AIProvider = 'openai' | 'anthropic';

export interface AIConfig {
  apiKey: string;
  provider: AIProvider;
  model?: string;
  autoAnalyze?: boolean; // Auto-analyze results (explanations, patterns, suggestions)
}

/**
 * Get AI API key from localStorage
 */
export function getAIApiKey(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(AI_API_KEY_STORAGE_KEY);
}

/**
 * Save AI API key to localStorage
 */
export function saveAIApiKey(apiKey: string): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(AI_API_KEY_STORAGE_KEY, apiKey);
}

/**
 * Remove AI API key from localStorage
 */
export function removeAIApiKey(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(AI_API_KEY_STORAGE_KEY);
}

/**
 * Get AI provider from localStorage
 */
export function getAIProvider(): AIProvider {
  if (typeof window === 'undefined') return 'openai';
  const provider = localStorage.getItem(AI_PROVIDER_STORAGE_KEY);
  return (provider as AIProvider) || 'openai';
}

/**
 * Save AI provider to localStorage
 */
export function saveAIProvider(provider: AIProvider): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(AI_PROVIDER_STORAGE_KEY, provider);
}

/**
 * Get AI model from localStorage
 */
export function getAIModel(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(AI_MODEL_STORAGE_KEY);
}

/**
 * Save AI model to localStorage
 */
export function saveAIModel(model: string): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(AI_MODEL_STORAGE_KEY, model);
}

/**
 * Get auto-analyze setting from localStorage
 */
export function getAIAutoAnalyze(): boolean {
  if (typeof window === 'undefined') return false;
  const value = localStorage.getItem(AI_AUTO_ANALYZE_STORAGE_KEY);
  return value === 'true';
}

/**
 * Save auto-analyze setting to localStorage
 */
export function saveAIAutoAnalyze(enabled: boolean): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(AI_AUTO_ANALYZE_STORAGE_KEY, enabled.toString());
}

/**
 * Get full AI configuration
 */
export function getAIConfig(): AIConfig | null {
  const apiKey = getAIApiKey();
  if (!apiKey) return null;

  const provider = getAIProvider();
  const model = getAIModel() || undefined;
  const autoAnalyze = getAIAutoAnalyze();

  return {
    apiKey,
    provider,
    model,
    autoAnalyze
  };
}

/**
 * Save full AI configuration
 */
export function saveAIConfig(config: AIConfig): void {
  saveAIApiKey(config.apiKey);
  saveAIProvider(config.provider);
  if (config.model) {
    saveAIModel(config.model);
  } else {
    localStorage.removeItem(AI_MODEL_STORAGE_KEY);
  }
  if (config.autoAnalyze !== undefined) {
    saveAIAutoAnalyze(config.autoAnalyze);
  }
}

/**
 * Check if AI is configured
 */
export function isAIConfigured(): boolean {
  return getAIApiKey() !== null;
}
